import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeCodeForToken, loadDraft } from '../lib/githubAuth';
import './WriteJekyllPost.css';

const ALLOWED_GITHUB_LOGIN = 'calicorone';

// UTF-8-safe base64 for draft content (handles Korean, emoji, etc.)
function base64Encode(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}

function CallbackErrorFallback({ error, onBack }) {
  return (
    <div className="write-jekyll-wrap">
      <div className="write-jekyll-inner">
        <p className="write-jekyll-error">
          화면을 불러오는 중 오류가 발생했습니다.
          {error && <small>{String(error)}</small>}
        </p>
        <button type="button" className="write-jekyll-cancel" onClick={onBack}>
          글쓰기로 돌아가기
        </button>
      </div>
    </div>
  );
}

function GithubCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setStatus('error');
      setMessage('Authorization code가 없습니다.');
      return;
    }
    (async () => {
      try {
        const result = await exchangeCodeForToken(code);
        const token = result.token;
        if (!token) {
          setStatus('error');
          if (result.error === 'no_client_id') {
            setMessage(
              'GitHub OAuth Client ID가 설정되지 않았습니다. 배포 환경(Vercel 등)에 REACT_APP_GITHUB_OAUTH_CLIENT_ID를 추가한 뒤 다시 빌드·배포해 주세요.'
            );
          } else if (result.error === 'no_verifier') {
            setMessage(
              '이 탭에서는 인증을 완료할 수 없습니다. 반드시 koiro.me 글쓰기 페이지에서 "GitHub로 게시"를 누른 뒤, 그대로 리다이렉트되는 같은 탭에서 GitHub 로그인까지 진행해 주세요. (다른 탭에서 열거나 링크를 복사하면 안 됩니다.)'
            );
          } else if (result.error === 'api_405') {
            setMessage(
              '토큰 교환 API가 호출되지 않았습니다 (405). Vercel 프로젝트 설정에서 Root Directory가 비어 있는지(레포 루트 배포) 확인한 뒤 다시 배포해 주세요.'
            );
          } else {
            setMessage(
              'GitHub에서 토큰을 거부했습니다. GitHub OAuth 앱 설정에서 Authorization callback URL이 이 사이트 주소와 정확히 일치하는지 확인해 주세요. (예: https://koiro.me/dev-blog/github-callback)'
            );
          }
          return;
        }
        const userRes = await fetch('https://api.github.com/user', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userText = await userRes.text();
        let user = {};
        if (userText) {
          try {
            user = JSON.parse(userText);
          } catch {
            setStatus('error');
            setMessage('GitHub 사용자 정보를 읽지 못했습니다.');
            return;
          }
        }
        if (user?.login !== ALLOWED_GITHUB_LOGIN) {
          setStatus('error');
          setMessage('이 계정은 게시할 수 없습니다. (calicorone만 가능)');
          return;
        }
        const draft = loadDraft();
        if (!draft?.title || !draft?.content || !draft?.slug || !draft?.date) {
          setStatus('error');
          setMessage('저장된 글이 없습니다. 다시 작성해 주세요.');
          return;
        }
        const path = `content/posts/${draft.date}-${draft.slug}.md`;
        const contentBase64 = base64Encode(draft.content);
        const putRes = await fetch(
          `https://api.github.com/repos/calicorone/koiro-dev-blog/contents/${path}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github.v3+json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: `Add post: ${draft.title}`,
              content: contentBase64,
              branch: 'master',
            }),
          }
        );
        if (!putRes.ok) {
          const err = await putRes.text();
          setStatus('error');
          setMessage('GitHub에 올리지 못했습니다.');
          setUrl(err);
          return;
        }
        setStatus('ok');
        setUrl(`https://blog.koiro.me/${draft.date.replace(/-/g, '/')}/${draft.slug}/`);
      } catch (e) {
        setStatus('error');
        const msg = e?.message || String(e) || '';
        const isNetworkError = msg === 'Failed to fetch' || e?.name === 'TypeError';
        const isJsonError = /json|JSON|Unexpected end/.test(msg);
        setMessage(
          isNetworkError
            ? '네트워크 요청에 실패했습니다. 인터넷 연결을 확인하고, VPN/방화벽을 잠시 끄고 다시 시도해 주세요.'
            : isJsonError
              ? '서버 응답을 읽지 못했습니다. 잠시 후 다시 시도해 주세요.'
              : msg || '오류가 발생했습니다.'
        );
      }
    })();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="write-jekyll-wrap">
        <div className="write-jekyll-inner">
          <p className="write-jekyll-required-hint">GitHub에 게시 중…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="write-jekyll-wrap">
      <div className="write-jekyll-inner">
        {status === 'ok' && (
          <p className="write-jekyll-success">
            게시되었습니다. <a href={url} target="_blank" rel="noopener noreferrer">blog.koiro.me에서 보기</a> (반영까지 1–2분 걸릴 수 있습니다).
          </p>
        )}
        {status === 'error' && (
          <p className="write-jekyll-error">
            {message}
            {url && <small>{url}</small>}
          </p>
        )}
        <button type="button" className="write-jekyll-cancel" onClick={() => navigate('/dev-blog/write-jekyll')}>
          글쓰기로 돌아가기
        </button>
      </div>
    </div>
  );
}

class GithubCallbackErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <CallbackErrorFallback
          error={this.state.error?.message}
          onBack={() => window.location.assign('/dev-blog/write-jekyll')}
        />
      );
    }
    return this.props.children;
  }
}

export default GithubCallback;
export { GithubCallbackErrorBoundary };
